import React from "react";
import {
  Box,
  Button,
  Typography,
  Paper,
  useTheme,
  alpha,
  Stack,
  Chip,
  Grid,
} from "@mui/material";
import ViewKanbanOutlinedIcon from "@mui/icons-material/ViewKanbanOutlined";
import AddIcon from "@mui/icons-material/Add";
import AssignmentTurnedInOutlinedIcon from "@mui/icons-material/AssignmentTurnedInOutlined";
import GroupsOutlinedIcon from "@mui/icons-material/GroupsOutlined";
import ForumOutlinedIcon from "@mui/icons-material/ForumOutlined";
import { useTranslation } from "react-i18next";

const EmptyStateTeamBoards = ({ onCreateClick }) => {
  const { t } = useTranslation("teamTasks");
  const theme = useTheme();
  const isDark = theme.palette.mode === "dark";
  const primary = theme.palette.primary.main;
  const primaryLight = alpha(primary, isDark ? 0.2 : 0.12);
  const primaryBg = alpha(primary, isDark ? 0.08 : 0.06);

  const chips = [
    { icon: <GroupsOutlinedIcon sx={{ fontSize: 20 }} />, label: t("emptyStateChipTeam") },
    { icon: <ForumOutlinedIcon sx={{ fontSize: 20 }} />, label: t("emptyStateChipChannel") },
    { icon: <AssignmentTurnedInOutlinedIcon sx={{ fontSize: 20 }} />, label: t("emptyStateChipRequests") },
  ];

  return (
    <Paper
      variant="outlined"
      sx={{
        width: "100%",
        overflow: "hidden",
        borderRadius: 3,
        border: `1px solid ${alpha(primary, isDark ? 0.25 : 0.18)}`,
        background: isDark
          ? `linear-gradient(160deg, ${alpha(theme.palette.background.paper, 0.98)} 0%, ${alpha(primary, 0.06)} 50%, ${alpha(theme.palette.background.paper, 0.98)} 100%)`
          : `linear-gradient(160deg, ${theme.palette.background.paper} 0%, ${primaryBg} 40%, ${theme.palette.background.paper} 100%)`,
        boxShadow: isDark
          ? `0 12px 40px ${alpha(theme.palette.common.black, 0.35)}, inset 0 1px 0 ${alpha(theme.palette.common.white, 0.05)}`
          : `0 12px 40px ${alpha(primary, 0.1)}, inset 0 1px 0 ${alpha(theme.palette.common.white, 0.8)}`,
      }}
    >
      <Box sx={{ p: { xs: 3, sm: 4 } }}>
        <Grid container spacing={4} alignItems="stretch">
          <Grid item xs={12} md={7}>
            <Box sx={{ height: "100%", display: "flex", flexDirection: "column" }}>
              <Box sx={{ display: "flex", alignItems: "center", gap: 2, mb: 2 }}>
                <Box
                  sx={{
                    position: "relative",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    width: 64,
                    height: 64,
                    flexShrink: 0,
                    borderRadius: "50%",
                    bgcolor: primaryLight,
                    "&::before": {
                      content: '""',
                      position: "absolute",
                      inset: -4,
                      borderRadius: "50%",
                      border: `2px solid ${alpha(primary, 0.2)}`,
                    },
                  }}
                >
                  <ViewKanbanOutlinedIcon sx={{ fontSize: 36 }} color="primary" />
                </Box>
                <Box>
                  <Typography
                    variant="overline"
                    sx={{ color: "primary.main", fontWeight: 700, letterSpacing: 1.1 }}
                  >
                    {t("boardListTitle")}
                  </Typography>
                  <Typography
                    variant="h6"
                    component="h2"
                    sx={{ fontWeight: 700, color: theme.palette.text.primary, mt: 0.25 }}
                  >
                    {t("emptyStateTitle")}
                  </Typography>
                </Box>
              </Box>
              <Typography
                variant="body2"
                color="text.secondary"
                sx={{ lineHeight: 1.75, mb: 2, flex: 1 }}
              >
                {t("emptyStateDescription")}
              </Typography>
              <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                {chips.map((item, idx) => (
                  <Chip
                    key={idx}
                    icon={item.icon}
                    label={item.label}
                    size="small"
                    variant="outlined"
                    sx={{
                      borderColor: alpha(primary, 0.35),
                      color: theme.palette.text.secondary,
                      "& .MuiChip-icon": { color: "primary.main" },
                    }}
                  />
                ))}
              </Stack>
            </Box>
          </Grid>

          <Grid item xs={12} md={5}>
            <Box
              sx={{
                height: "100%",
                display: "flex",
                flexDirection: "column",
                justifyContent: "center",
                gap: 2,
              }}
            >
              <Box
                sx={{
                  py: 1.5,
                  px: 2,
                  borderRadius: 2,
                  bgcolor: alpha(primary, isDark ? 0.08 : 0.06),
                  borderLeft: `4px solid ${primary}`,
                }}
              >
                <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 500, lineHeight: 1.6 }}>
                  {t("emptyStateTip")}
                </Typography>
              </Box>
              <Button
                variant="contained"
                size="large"
                fullWidth
                startIcon={<AddIcon />}
                onClick={onCreateClick}
                sx={{
                  py: 1.5,
                  fontWeight: 600,
                  boxShadow: 2,
                  borderRadius: 2,
                  "&:hover": { boxShadow: 4 },
                }}
              >
                {t("createBoard")}
              </Button>
            </Box>
          </Grid>
        </Grid>
      </Box>
    </Paper>
  );
};

export default EmptyStateTeamBoards;
